<template>
  <div
    v-if="isPanelOpen"
    class="fixed inset-0 z-[9999] flex justify-end"
    @click.self="closePanel"
  >
    <div
      class="bg-n-background border-l border-n-weak w-[420px] max-w-full h-full flex flex-col shadow-2xl animate-slideIn rounded-l-2xl overflow-hidden"
    >
      <div
        class="flex items-center px-5 py-3.5 gap-2 flex-shrink-0 border-b border-n-weak bg-n-solid-2"
      >
        <button
          v-if="activeConversationId && activeTab === 'chat'"
          class="flex items-center justify-center size-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
          @click="goBack"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div class="flex-1 min-w-0">
          <h3
            class="text-base font-bold text-n-slate-12 m-0 truncate"
          >
            {{ currentHeaderTitle }}
          </h3>
          <p
            v-if="activeConversationId && otherUserStatus && activeTab === 'chat'"
            class="text-xs text-n-slate-10 m-0 mt-0.5"
          >
            {{ otherUserStatus }}
          </p>
          <p
            v-if="activeTab === 'support'"
            class="text-xs text-n-slate-10 m-0 mt-0.5 flex items-center gap-1"
          >
            <span class="size-1.5 rounded-full bg-green-400 inline-block animate-pulse" />
            Estamos online
          </p>
        </div>
        <button
          v-if="isAdmin && !activeConversationId && activeTab === 'chat'"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white hover:brightness-110 transition-all shadow-sm"
          style="background: linear-gradient(135deg, #3b82f6, #4f46e5);"
          title="Enviar alerta para agentes"
          @click="showBroadcastModal = true"
        >
          📢 Alerta
        </button>
        <button
          class="flex items-center justify-center size-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
          @click="closePanel"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <!-- Content area -->
      <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
        <template v-if="activeTab === 'chat'">
          <ChatList
            v-if="!activeConversationId"
            @select="selectConversation"
          />
          <ChatMessages
            v-else
            :conversation-id="activeConversationId"
          />
        </template>
        <SupportWidget v-else-if="activeTab === 'support' && hasSupportWidget" />
      </div>

      <!-- Bottom tab bar -->
      <div v-if="hasSupportWidget" class="flex shrink-0 gap-px border-t border-n-weak bg-n-weak">
        <button
          class="flex-1 flex items-center justify-center gap-2 py-3 text-xs font-semibold transition-all cursor-pointer border-none"
          :class="activeTab === 'chat' ? 'text-white' : 'text-n-slate-11 bg-n-background hover:bg-n-alpha-2'"
          :style="activeTab === 'chat' ? { background: '#3b82f6' } : {}"
          @click="activeTab = 'chat'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Chat interno
        </button>
        <button
          class="flex-1 flex items-center justify-center gap-2 py-3 text-xs font-semibold transition-all cursor-pointer border-none"
          :class="activeTab === 'support' ? 'text-white' : 'text-n-slate-11 bg-n-background hover:bg-n-alpha-2'"
          :style="activeTab === 'support' ? { background: '#22c55e' } : {}"
          @click="activeTab = 'support'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" />
          </svg>
          Suporte
        </button>
      </div>
    </div>

    <BroadcastSendModal
      :is-open="showBroadcastModal"
      @close="showBroadcastModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import ChatList from './ChatList.vue';
import ChatMessages from './ChatMessages.vue';
import BroadcastSendModal from './BroadcastSendModal.vue';
import SupportWidget from '../SupportWidget.vue';

const store = useStore();
const showBroadcastModal = ref(false);
const activeTab = ref('chat');

const hasSupportWidget = computed(() =>
  !!(window.chatwootConfig?.supportWidgetToken)
);

const isPanelOpen = computed(
  () => store.getters['internalChat/isPanelOpen']
);
const activeConversationId = computed(
  () => store.getters['internalChat/getActiveConversationId']
);
const isAdmin = computed(() => {
  const currentUser = store.getters.getCurrentUser;
  const accountId = store.getters.getCurrentAccountId;
  if (!currentUser || !accountId) return false;
  return currentUser.role === 'administrator';
});

const headerTitle = computed(() => {
  if (!activeConversationId.value) return '💬 Chat Interno';
  const conversations =
    store.getters['internalChat/getConversations'];
  const conv = conversations.find(
    c => c.id === activeConversationId.value
  );
  if (conv?.system) return conv.system_name || '🔔 Notificações CRM';
  return conv?.other_user?.name || 'Conversa';
});

const currentHeaderTitle = computed(() => {
  if (activeTab.value === 'support') return '🎧 Suporte OhMyBot';
  return headerTitle.value;
});

const otherUserStatus = computed(() => {
  if (!activeConversationId.value) return null;
  const conversations =
    store.getters['internalChat/getConversations'];
  const conv = conversations.find(
    c => c.id === activeConversationId.value
  );
  if (conv?.system) return null;
  return conv?.other_user?.availability === 'online'
    ? '🟢 Online'
    : '⚪ Offline';
});

function selectConversation(id) {
  store.dispatch('internalChat/setActiveConversation', id);
}

function goBack() {
  store.dispatch('internalChat/setActiveConversation', null);
}

function closePanel() {
  store.dispatch('internalChat/closePanel');
}
</script>

<style>
@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.animate-slideIn {
  animation: slideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>
